import React, { useState, useEffect, useCallback } from 'react';
import { InvoicesContextObject, InvoicesContextType } from './InvoicesContextObject';
import { Invoice } from '@cleanpro/common-ui';
import { supabase } from '@cleanpro/common-ui';
import { CreateInvoiceInput, UpdateInvoiceInput } from '../types';

/**
 * Transforms a database row to an Invoice entity
 */
const transformInvoiceRow = (row: Record<string, unknown>): Invoice => {
  // Handle line_items: it's stored as jsonb in the database, so it's already an object
  let lineItems = [];
  if (row.line_items) {
    if (typeof row.line_items === 'string') {
      try {
        lineItems = JSON.parse(row.line_items);
      } catch (e) {
        console.error('Error parsing line_items:', e);
        lineItems = [];
      }
    } else {
      lineItems = row.line_items as unknown[];
    }
  }

  // Extract customer data from joined table
  const customer = row.customers as Record<string, unknown> | null;
  
  // Extract property data from joined table (via jobs)
  const job = row.jobs as Record<string, unknown> | null;
  const property = job?.properties as Record<string, unknown> | null;
  
  // Build address string from property
  let customerAddress: string | undefined;
  if (property) {
    const parts = [
      property.address_line_1,
      property.address_line_2,
      property.city,
      property.state,
      property.postal_code
    ].filter(Boolean);
    customerAddress = parts.length > 0 ? parts.join(', ') : undefined;
  }

  return {
    id: row.id as string,
    customerId: row.customer_id as string,
    customerName: customer?.name as string | undefined,
    customerEmail: customer?.email as string | undefined,
    customerPhone: customer?.phone as string | undefined,
    customerAddress: customerAddress,
    jobId: (row.job_id as string | null) || undefined,
    invoiceNumber: (row.invoice_number as string | null) || undefined,
    totalAmount: row.amount as number,
    amount: row.amount as number,
    status: row.status as Invoice['status'],
    issueDate: row.issue_date as string,
    dueDate: row.due_date as string,
    lineItems,
    subtotal: (row.subtotal as number | null) || undefined,
    taxRate: (row.tax_rate as number | null) || undefined,
    taxAmount: (row.tax_amount as number | null) || undefined,
    discountAmount: (row.discount_amount as number | null) || undefined,
    notes: (row.notes as string | null) || undefined,
    createdAt: (row.created_at as string | null) || new Date().toISOString(),
    updatedAt: (row.updated_at as string | null) || new Date().toISOString(),
    finalizedAt: (row.finalized_at as string | null) || undefined,
    sentAt: (row.sent_at as string | null) || undefined,
    paidAt: (row.paid_at as string | null) || undefined,
    overdueAt: (row.overdue_at as string | null) || undefined,
  };
};

/**
 * Provides invoice data and actions to child components via React Context.
 *
 * This provider manages the state and CRUD operations for invoices, including:
 * - Fetching invoices from the database on mount
 * - Adding new invoices
 * - Updating existing invoices (including status changes)
 * - Refreshing the invoices list
 * - Handling loading and error states
 *
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - Child components to receive the context
 * @returns {JSX.Element} The provider wrapping its children
 */
export const InvoicesDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refreshInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('invoices')
        .select('*, customers(id, name, email, phone), jobs(id, properties(address_line_1, address_line_2, city, state, postal_code))')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      const transformedInvoices = (data || []).map(transformInvoiceRow);
      setInvoices(transformedInvoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadInvoices = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await supabase
          .from('invoices')
          .select('*, customers(id, name, email, phone), jobs(id, properties(address_line_1, address_line_2, city, state, postal_code))')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        
        if (isMounted) {
          const transformedInvoices = (data || []).map(transformInvoiceRow);
          setInvoices(transformedInvoices);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load invoices');
          console.error('Error fetching invoices:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInvoices();

    return () => {
      isMounted = false;
    };
  }, []);

  // Note: Real-time subscription removed - invoice changes don't need instant updates.
  // Data is refetched on component mount and when needed.

  const addInvoice = useCallback(async (invoice: CreateInvoiceInput): Promise<Invoice> => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: insertError } = await supabase
        .from('invoices')
        .insert({
          customer_id: invoice.customerId,
          job_id: invoice.jobId,
          amount: invoice.totalAmount,
          status: invoice.status,
          issue_date: invoice.issueDate,
          due_date: invoice.dueDate,
          invoice_number: invoice.invoiceNumber,
          line_items: invoice.lineItems ? JSON.stringify(invoice.lineItems) : null,
          subtotal: invoice.subtotal,
          tax_rate: invoice.taxRate,
          tax_amount: invoice.taxAmount,
          discount_amount: invoice.discountAmount,
          notes: invoice.notes,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      if (!data) throw new Error('No data returned from insert');

      const newInvoice = transformInvoiceRow(data as Record<string, unknown>);
      setInvoices((prev) => [newInvoice, ...prev]);
      
      return newInvoice;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create invoice';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateInvoice = useCallback(async (invoiceId: string, invoiceData: UpdateInvoiceInput): Promise<Invoice> => {
    try {
      setLoading(true);
      setError(null);

      const updateData: Record<string, unknown> = {};
      if (invoiceData.customerId !== undefined) updateData.customer_id = invoiceData.customerId;
      if (invoiceData.jobId !== undefined) updateData.job_id = invoiceData.jobId;
      if (invoiceData.totalAmount !== undefined) updateData.amount = invoiceData.totalAmount;
      if (invoiceData.status !== undefined) updateData.status = invoiceData.status;
      if (invoiceData.issueDate !== undefined) updateData.issue_date = invoiceData.issueDate;
      if (invoiceData.dueDate !== undefined) updateData.due_date = invoiceData.dueDate;
      if (invoiceData.invoiceNumber !== undefined) updateData.invoice_number = invoiceData.invoiceNumber;
      if (invoiceData.lineItems !== undefined) updateData.line_items = JSON.stringify(invoiceData.lineItems);
      if (invoiceData.subtotal !== undefined) updateData.subtotal = invoiceData.subtotal;
      if (invoiceData.taxRate !== undefined) updateData.tax_rate = invoiceData.taxRate;
      if (invoiceData.taxAmount !== undefined) updateData.tax_amount = invoiceData.taxAmount;
      if (invoiceData.discountAmount !== undefined) updateData.discount_amount = invoiceData.discountAmount;
      if (invoiceData.notes !== undefined) updateData.notes = invoiceData.notes;

      const { data, error: updateError } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', invoiceId)
        .select()
        .single();

      if (updateError) throw updateError;
      if (!data) throw new Error('No data returned from update');

      const updatedInvoice = transformInvoiceRow(data as Record<string, unknown>);
      setInvoices((prev) => prev.map((inv) => (inv.id === invoiceId ? updatedInvoice : inv)));
      
      return updatedInvoice;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update invoice';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateInvoiceStatus = useCallback(async (invoiceId: string, status: Invoice['status']): Promise<Invoice> => {
    const updateData: Partial<UpdateInvoiceInput> = { status };
    
    // Add timestamp fields based on status
    const now = new Date().toISOString();
    if (status === 'sent') {
      updateData.sentAt = now;
    } else if (status === 'paid') {
      updateData.paidAt = now;
    } else if (status === 'overdue') {
      updateData.overdueAt = now;
    }

    return updateInvoice(invoiceId, updateData);
  }, [updateInvoice]);

  const approveInvoice = useCallback(async (invoiceId: string): Promise<Invoice> => {
    const now = new Date().toISOString();
    return updateInvoice(invoiceId, { 
      status: 'sent',
      finalizedAt: now,
      sentAt: now 
    });
  }, [updateInvoice]);

  const deleteInvoice = useCallback(async (invoiceId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (deleteError) throw deleteError;

      setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete invoice';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getInvoiceById = useCallback((invoiceId: string): Invoice | undefined => {
    return invoices.find((inv) => inv.id === invoiceId);
  }, [invoices]);

  const value: InvoicesContextType = {
    invoices,
    loading,
    error,
    addInvoice,
    updateInvoice,
    updateInvoiceStatus,
    approveInvoice,
    deleteInvoice,
    refreshInvoices,
    getInvoiceById,
  };

  return (
    <InvoicesContextObject.Provider value={value}>
      {children}
    </InvoicesContextObject.Provider>
  );
};
